'use client'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { KeyRound, Loader2, RefreshCw, TestTube, Unlink } from 'lucide-react'
import { CrmProviderLogo } from './crm-provider-logo'
import type { CrmIntegrationRecord, CrmProviderInfo } from '@/hooks/integrations/use-crm-integrations'
import { formatCrmAccountHost } from '@/lib/crm/format-account-host'
import { useTranslations } from "next-intl"



interface CrmIntegrationItemProps {
  integration: CrmIntegrationRecord
  provider?: CrmProviderInfo
  onTest: (id: string) => void
  onToggle: (id: string, isActive: boolean) => void
  onDisconnect: (id: string) => void
  onUpdateCredentials: (integration: CrmIntegrationRecord) => void
  onReconnect: (integration: CrmIntegrationRecord) => void
  isTesting: boolean
  isToggling: boolean
  isDisconnecting: boolean
  isReconnecting: boolean
}

export function CrmIntegrationItem({
  integration,
  provider,
  onTest,
  onToggle,
  onDisconnect,
  onUpdateCredentials,
  onReconnect,
  isTesting,
  isToggling,
  isDisconnecting,
  isReconnecting,
}: CrmIntegrationItemProps) {

  const tUi = useTranslations("Crm.CrmCrmIntegrationItem")

  // OAuth rows can't have a key re-pasted — they re-authorize through the OAuth handshake. The
  // `authMode` marker is stamped on the row by the OAuth callback (credentials never reach the
  // client, so metadata is the only client-visible discriminator).
  const isOAuth = integration.metadata?.authMode === 'oauth'
  const getStatusBadge = () => {
    if (!integration.isActive) {
      return <Badge variant="secondary"><span key={tUi("disabled")}>{tUi("disabled")}</span></Badge>
    }
    return <Badge className="bg-green-100 text-green-800"><span key={tUi("connected")}>{tUi("connected")}</span></Badge>
  }

  const providerName = provider?.name || integration.provider
  const subdomain = integration.metadata?.subdomain as string | undefined
  const accountHost = subdomain
    ? formatCrmAccountHost(integration.provider, subdomain)
    : undefined

  return (
    <div className="min-w-0 space-y-3 rounded-md border bg-background p-4">
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div
            className="shrink-0 p-2 rounded-full"
            style={{ backgroundColor: provider ? `${provider.color}15` : '#f3f4f6' }}
          >
            <CrmProviderLogo
              provider={integration.provider}
              logoUrl={provider?.logoUrl}
              size={16}
            />
          </div>
          <div className="text-sm flex-1 min-w-0">
            <div className="font-medium [overflow-wrap:anywhere]">{integration.name}</div>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-muted-foreground [overflow-wrap:anywhere]">
              <span>{providerName}</span>
              {accountHost && <span className="min-w-0">({accountHost})</span>}
            </div>
          </div>
        </div>
        <div className="flex min-h-9 shrink-0 items-center">
          <Label htmlFor={`crm-toggle-${integration.id}`} className="sr-only">
            <span key={integration.isActive ? tUi("enabled") : tUi("disabled")}>{integration.isActive ? tUi("enabled") : tUi("disabled")}</span>
          </Label>
          <Switch
            id={`crm-toggle-${integration.id}`}
            checked={integration.isActive}
            title={integration.isActive ? tUi("disableIntegration") : tUi("enableIntegration")}
            onCheckedChange={(checked) => onToggle(integration.id, checked)}
            disabled={isToggling}
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span><span key={tUi("status")}>{tUi("status")}</span></span>
        {getStatusBadge()}
      </div>
      <div
        role="group"
        aria-label={tUi("actionsForValue0", { value0: String(integration.name) })}
        className="flex min-w-0 flex-wrap gap-2 border-t pt-3 [&>button]:h-auto [&>button]:min-h-9 [&>button]:min-w-0 [&>button]:flex-[1_1_9rem] [&>button]:whitespace-normal [&>button]:py-2"
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => onTest(integration.id)}
          disabled={isTesting}
        >
          {isTesting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <TestTube className="w-4 h-4" />
          )}
          <span key={isTesting ? tUi("testing") : tUi("test")}>{isTesting ? tUi("testing") : tUi("test")}</span>
        </Button>
        {isOAuth ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onReconnect(integration)}
            disabled={isReconnecting}
            title={tUi("reAuthorizeThisOAuthConnectionEGAfter")}
          >
            {isReconnecting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            <span key={isReconnecting ? tUi("reconnecting") : tUi("reconnect")}>{isReconnecting ? tUi("reconnecting") : tUi("reconnect")}</span>
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onUpdateCredentials(integration)}
            disabled={!provider}
            title={!provider ? tUi("providerInfoNotLoaded") : tUi("updateAPICredentials")}
          >
            <KeyRound className="w-4 h-4" />
            <span><span key={tUi("updateKey")}>{tUi("updateKey")}</span></span>
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDisconnect(integration.id)}
          disabled={isDisconnecting}
        >
          <Unlink className="w-4 h-4" />
          <span key={isDisconnecting ? tUi("disconnecting") : tUi("disconnect")}>{isDisconnecting ? tUi("disconnecting") : tUi("disconnect")}</span>
        </Button>
      </div>
    </div>
  )
}
